import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CopySimple, 
  ArrowsClockwise, 
  Copy,
  CheckCircle,
  FileText,
  Envelope,
  Megaphone,
  User,
  ShoppingCart,
  Globe,
  Rocket
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShareButton } from '../components/ShareButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';

import { usePersistedInput, usePersistedOutput } from '../hooks/usePersistedState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const copyTypes = [
  { id: 'product_description', label: 'Product Description', icon: ShoppingCart, description: 'Compelling product copy' },
  { id: 'homepage', label: 'Homepage Copy', icon: Globe, description: 'Hero and value props' },
  { id: 'landing_page', label: 'Landing Page', icon: FileText, description: 'Conversion-focused copy' },
  { id: 'about', label: 'About Page', icon: User, description: 'Brand story and mission' },
  { id: 'email_newsletter', label: 'Email Newsletter', icon: Envelope, description: 'Newsletter content' },
  { id: 'ad_copy', label: 'Ad Copy', icon: Megaphone, description: 'Social & PPC ads' },
  { id: 'social_bio', label: 'Social Bio', icon: User, description: 'Profile descriptions' }
];

const tones = ['Professional', 'Casual', 'Witty', 'Inspirational', 'Bold', 'Friendly', 'Luxurious', 'Playful'];

export const CopyStudioPage = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = usePersistedInput('copy-studio', 'selectedType', null);
  const [productService, setProductService] = usePersistedInput('copy-studio', 'productService', '');
  const [brandTone, setBrandTone] = usePersistedInput('copy-studio', 'brandTone', '');
  const [audience, setAudience] = usePersistedInput('copy-studio', 'audience', '');
  const [offer, setOffer] = usePersistedInput('copy-studio', 'offer', '');
  const [objective, setObjective] = usePersistedInput('copy-studio', 'objective', '');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = usePersistedOutput('copy-studio', null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [tab, setTab] = useState('create');
  // Zero Friction: hide brand_tone/audience/offer/objective behind Customise →
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const prefill = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user/profile`);
        const bp = data.brand_profile;
        if (bp) {
          if (bp.description) setProductService(prev => prev || bp.description);
          if (bp.target_audience) setAudience(prev => prev || bp.target_audience);
          if (bp.tone_of_voice) setBrandTone(prev => prev || bp.tone_of_voice);
        }
      } catch (e) { /* ignore */ }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateCopy = async () => {
    if (!selectedType || !productService.trim()) {
      toast.error("Tell us what you're promoting first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/copy/generate`, {
        copy_type: selectedType,
        product_service: productService,
        brand_tone: brandTone || 'Friendly',
        audience: audience || 'general audience',
        offer,
        objective
      });

      setResults(data.copy);
    } catch (err) {
      console.error('Failed to generate copy:', err);
      setResults(null);
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error('Add a brand_tone in Customise — we need the voice.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not write the copy. Try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const setMockResults = () => {
    const mockData = {
      product_description: {
        short: 'Premium organic skincare that transforms your daily routine. Made with natural ingredients that nourish and protect.',
        medium: 'Discover the power of nature with our premium organic skincare line. Each product is carefully crafted using sustainably sourced ingredients that work in harmony with your skin. Experience visible results without harsh chemicals – because your skin deserves better.',
        long: 'Transform your skincare routine with our award-winning organic collection. Developed by dermatologists and powered by nature, each formula combines science with time-tested botanical ingredients. Our commitment to sustainability means every product is ethically sourced, cruelty-free, and packaged in recyclable materials. Join thousands of customers who have discovered healthier, more radiant skin. Your journey to natural beauty starts here.'
      },
      homepage: {
        hero_headline: 'Skincare That Actually Works',
        hero_subheadline: 'Clean, effective formulas backed by science and loved by thousands',
        value_props: ['100% organic ingredients', 'Dermatologist approved', 'Results in 14 days', 'Free shipping over $50'],
        cta: 'Shop the Collection'
      },
      landing_page: {
        headline: 'Get Clear, Glowing Skin in Just 2 Weeks',
        subheadline: 'Join 50,000+ customers who transformed their skin with our proven 3-step system',
        benefits: ['Visible results guaranteed', 'Works for all skin types', 'No harsh chemicals', '30-day money-back guarantee'],
        social_proof: '"Best skincare I\'ve ever used" – 4.9/5 from 12,000+ reviews',
        cta: 'Start Your Transformation'
      },
      about: {
        story: 'We started with a simple belief: everyone deserves access to effective, clean skincare. After years of frustration with products full of unpronounceable ingredients, we decided to create something different.',
        mission: 'Our mission is to make premium organic skincare accessible to everyone, while respecting both your skin and our planet.',
        values: ['Transparency in every ingredient', 'Sustainability in every decision', 'Results that speak for themselves']
      },
      email_newsletter: {
        subject_lines: ['Your skin will thank you for this', 'The secret ingredient dermatologists love', 'New arrivals you need to see'],
        preview_text: 'Plus: 20% off your next order inside',
        body: 'Hi [Name],\n\nWe\'ve been working on something special, and we couldn\'t wait to share it with you.\n\nIntroducing our new Vitamin C Serum – formulated with 15% pure vitamin C to brighten, protect, and transform your skin.\n\nEarly reviews are in, and customers are calling it "a game-changer."\n\nAs a thank you for being part of our community, here\'s an exclusive 20% off your first order.\n\nShop now and see the difference for yourself.',
        cta: 'Shop Now & Save 20%'
      },
      ad_copy: [
        { headline: 'Clear Skin Starts Here', primary_text: 'Tired of skincare that doesn\'t work? Our organic formulas are backed by science and loved by 50,000+ customers. Try risk-free today.', cta: 'Shop Now' },
        { headline: 'Your Skin Deserves Better', primary_text: 'Ditch the chemicals. Embrace clean beauty. See results in 14 days or your money back. Join the skincare revolution.', cta: 'Learn More' },
        { headline: 'Glow Up in 2 Weeks', primary_text: 'Real results. Real ingredients. Real happy customers. Discover why everyone\'s switching to organic skincare.', cta: 'Get Started' }
      ],
      social_bio: [
        '✨ Clean beauty that works | Organic skincare backed by science | Shop our bestsellers ⬇️',
        'Transforming skin, naturally 🌿 | 50K+ happy customers | Free shipping on orders $50+',
        'Your skin deserves better 💚 | Organic • Effective • Sustainable | Shop the glow ⬇️'
      ]
    };
    setResults(mockData[selectedType] || {});
  };

  const handleHistoryReload = (item) => {
    const inputs = item.inputs || {};
    if (inputs.copy_type) setSelectedType(inputs.copy_type);
    if (inputs.product_service) setProductService(inputs.product_service);
    if (inputs.brand_tone) setBrandTone(inputs.brand_tone);
    if (inputs.audience) setAudience(inputs.audience);
    if (inputs.offer) setOffer(inputs.offer);
    if (inputs.objective) setObjective(inputs.objective);
    const output = item.output || {};
    if (output.copy) setResults(output.copy);
    setTab('create');
    toast.success('Loaded from history');
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const renderResults = () => {
    if (!results) return null;

    switch (selectedType) {
      case 'product_description':
        return (
          <div className="space-y-4">
            {['short', 'medium', 'long'].map((length) => (
              <div key={length} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 mb-2 inline-block capitalize">{length}</span>
                    <p className="text-zinc-200">{results[length]}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(results[length], length)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiedKey === length ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        );

      case 'homepage':
      case 'landing_page':
        return (
          <div className="space-y-4">
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Headline</span>
              <div className="flex items-start justify-between gap-4 mt-2">
                <h3 className="text-xl font-medium">{results.headline || results.hero_headline}</h3>
                <button
                  onClick={() => copyToClipboard(results.headline || results.hero_headline, 'headline')}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiedKey === 'headline' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                </button>
              </div>
            </div>
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Subheadline</span>
              <div className="flex items-start justify-between gap-4 mt-2">
                <p className="text-zinc-300">{results.subheadline || results.hero_subheadline}</p>
                <button
                  onClick={() => copyToClipboard(results.subheadline || results.hero_subheadline, 'subheadline')}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiedKey === 'subheadline' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                </button>
              </div>
            </div>
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Value Props / Benefits</span>
              <ul className="mt-2 space-y-2">
                {(results.benefits || results.value_props || []).map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-zinc-300">
                    <CheckCircle size={16} className="text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {results.social_proof && (
              <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <span className="text-xs text-zinc-500 uppercase tracking-wider">Social Proof</span>
                <p className="text-zinc-300 mt-2">{results.social_proof}</p>
              </div>
            )}
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">CTA</span>
              <div className="flex items-center justify-between mt-2">
                <span className="px-4 py-2 bg-white text-black rounded-full font-medium">{results.cta}</span>
                <button
                  onClick={() => copyToClipboard(results.cta, 'cta')}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiedKey === 'cta' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                </button>
              </div>
            </div>
          </div>
        );

      case 'about':
        return (
          <div className="space-y-4">
            {['story', 'mission'].map((key) => (
              <div key={key} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-xs text-zinc-500 uppercase tracking-wider capitalize">{key}</span>
                    <p className="text-zinc-200 mt-2">{results[key]}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(results[key], key)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiedKey === key ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            ))}
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Values</span>
              <ul className="mt-2 space-y-2">
                {(results.values || []).map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-zinc-300">
                    <CheckCircle size={16} className="text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'email_newsletter':
        return (
          <div className="space-y-4">
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Subject Lines</span>
              <div className="mt-2 space-y-2">
                {(results.subject_lines || []).map((line, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 p-2 bg-zinc-900/50 rounded-lg">
                    <span className="text-zinc-200">{typeof line === 'object' ? (line.line || line.text || JSON.stringify(line)) : String(line)}</span>
                    <button
                      onClick={() => copyToClipboard(line, `subject-${i}`)}
                      className="p-1 text-zinc-400 hover:text-white transition-colors"
                    >
                      {copiedKey === `subject-${i}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <span className="text-xs text-zinc-500 uppercase tracking-wider">Email Body</span>
                  <p className="text-zinc-200 mt-2 whitespace-pre-line">{results.body}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(results.body, 'body')}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiedKey === 'body' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                </button>
              </div>
            </div>
          </div>
        );

      case 'ad_copy':
        return (
          <div className="space-y-4">
            {Array.isArray(results) && results.map((ad, i) => (
              <div key={i} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 mb-2 inline-block">Variation {i + 1}</span>
                    <h3 className="font-medium mb-2">{ad.headline}</h3>
                    <p className="text-zinc-300 text-sm mb-2">{ad.primary_text}</p>
                    <span className="text-xs text-zinc-500">CTA: {ad.cta}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${ad.headline}\n\n${ad.primary_text}\n\nCTA: ${ad.cta}`, `ad-${i}`)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiedKey === `ad-${i}` ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        );

      case 'social_bio':
        return (
          <div className="space-y-3">
            {Array.isArray(results) && results.map((bio, i) => (
              <div key={i} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
                <p className="text-zinc-200">{bio}</p>
                <button
                  onClick={() => copyToClipboard(bio, `bio-${i}`)}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiedKey === `bio-${i}` ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                </button>
              </div>
            ))}
          </div>
        );

      default:
        return <pre className="text-zinc-400 text-sm overflow-auto">{JSON.stringify(results, null, 2)}</pre>;
    }
  };

  const CreateNowBar = () => (
    <div className="flex flex-wrap gap-2 mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <span className="text-[10px] uppercase tracking-wider self-center mr-1" style={{ color: 'rgba(245,240,232,0.2)' }}>Create now:</span>
      {[
        { label: 'Carousel', path: '/dashboard/carousel-creator' },
        { label: 'Video Ad', path: '/dashboard/ad-creator' },
        { label: 'Flyer', path: '/dashboard/template-studio' },
        { label: 'Social Post', path: '/dashboard/content-studio' },
      ].map(l => (
        <button key={l.path} onClick={() => navigate(l.path)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all hover:opacity-90"
          style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.1)' }}>
          {l.label}
        </button>
      ))}
    </div>
  );

  return (
    <DashboardLayout>
      <div data-testid="copy-studio-page" className="space-y-8">
        <BrandSettingsSummary fields={['brand','tone','audience']} />
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <CopySimple size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Copy Studio</h1>
              <p className="text-zinc-400 text-sm">Ad copy, headlines, and sales writing — done for you</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Template Selection */}
        {tab === 'create' && !selectedType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Choose a template</h2>

            {/* Launch Page Promo */}
            <button
              onClick={() => navigate('/dashboard/launch-page')}
              data-testid="launch-page-promo"
              className="w-full bg-gradient-to-r from-[#c8f060]/10 via-[#c8f060]/5 to-transparent border border-[#c8f060]/20 rounded-2xl p-5 text-left hover:border-[#c8f060]/40 transition-all group mb-5 flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-[#c8f060]/10 flex items-center justify-center shrink-0 group-hover:bg-[#c8f060]/20 transition-colors">
                <Rocket size={24} className="text-[#c8f060]" weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white">Launch Page</h3>
                <p className="text-sm text-zinc-400">Create a complete landing page for your business in seconds</p>
              </div>
              <span className="text-xs text-zinc-500 shrink-0 hidden sm:block">Structured copy + live preview</span>
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {copyTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    data-testid={`template-${type.id}`}
                    className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 text-left hover:bg-zinc-900/80 hover:border-white/20 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center mb-4 group-hover:bg-white/10 transition-colors">
                      <Icon size={20} className="text-white" />
                    </div>
                    <h3 className="font-medium mb-1">{type.label}</h3>
                    <p className="text-sm">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Generate Form */}
        {tab === 'create' && selectedType && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5 sticky top-24">
                <div className="flex items-center justify-between">
                  <h2 className="font-medium font-['Instrument_Serif',serif]">
                    {copyTypes.find(t => t.id === selectedType)?.label}
                  </h2>
                  <button
                    onClick={() => { setSelectedType(null); setResults(null); setShowAdvanced(false); }}
                    className="text-zinc-400 hover:text-white text-sm"
                    data-testid="change-copy-type"
                  >
                    Change →
                  </button>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>What are you promoting?</label>
                  <input
                    type="text"
                    value={productService}
                    onChange={(e) => setProductService(e.target.value)}
                    data-testid="product-input"
                    placeholder="e.g., my coaching service, this week's flash sale, my SaaS product"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>

                <button
                  onClick={generateCopy}
                  disabled={loading || !productService}
                  data-testid="generate-copy-btn"
                  className="w-full rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  style={{ background: '#c8f060', color: '#080808' }}
                >
                  {loading ? (
                    <>
                      <ArrowsClockwise size={18} className="animate-spin" />
                      Writing your copy…
                    </>
                  ) : (
                    <>
                      <CopySimple size={18} weight="fill" />
                      Write my copy
                    </>
                  )}
                </button>

                <button onClick={() => setShowAdvanced(v => !v)} data-testid="copy-customise-toggle"
                  className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {showAdvanced ? 'Hide customise' : 'Customise →'}
                  </span>
                  <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
                </button>

                {showAdvanced && (
                  <div className="space-y-4 pt-1">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Brand Tone</label>
                      <Select value={brandTone} onValueChange={setBrandTone}>
                        <SelectTrigger data-testid="tone-select" className="w-full bg-zinc-900 border-white/10 text-white">
                          <SelectValue placeholder="Select tone" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          {tones.map((t) => (
                            <SelectItem key={t} value={t} className="text-white hover:bg-white/10">
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Target Audience</label>
                      <input
                        type="text"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        data-testid="audience-input"
                        placeholder="e.g., Women 25-45, skincare enthusiasts"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Offer (optional)</label>
                      <input
                        type="text"
                        value={offer}
                        onChange={(e) => setOffer(e.target.value)}
                        placeholder="e.g., 20% off first order"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Objective (optional)</label>
                      <input
                        type="text"
                        value={objective}
                        onChange={(e) => setObjective(e.target.value)}
                        placeholder="e.g., Drive purchases, build email list"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <h2 className="font-medium font-['Instrument_Serif',serif] mb-6">Your Copy</h2>

                {!results ? (
                  <div className="text-center py-16">
                    <CopySimple size={48} className="mx-auto mb-4 text-zinc-700" />
                    <h3 className="text-lg font-medium mb-2">Tap "Write my copy" above</h3>
                    <p className="text-sm">Fill in your details and click generate</p>
                  </div>
                ) : (
                  <>
                    {renderResults()}
                    <CreateNowBar />
                  </>
                )}

                {results && (
                  <button
                    onClick={generateCopy}
                    disabled={loading}
                    className="mt-6 w-full py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
                    Regenerate
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Generation History</h2>
            <TextHistoryPanel toolType="copy_studio" onReload={handleHistoryReload} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CopyStudioPage;
